import { useRouter } from 'next/compat/router';

import { MinimumWorkspace } from '@repo/api-client';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
  SubscriptionUpgradeAccess,
  BillingAdminAccess,
} from '@repo/subscription/client';
import IconAddUser from '@repo/ui-icons/IconAddUser';
import IconPremiumColored from '@repo/ui-icons/IconPremiumColored';
import IconSettings from '@repo/ui-icons/IconSettings';
import { Avatar } from '@repo/ui-kit/components/core/Avatars';
import { Badge } from '@repo/ui-kit/components/core/Badge';
import { Button } from '@repo/ui-kit/components/core/Button';
import { Popover } from '@repo/ui-kit/components/core/Popover';

import { useWorkspaceInviteDialog } from 'components/WorkspaceInviteDialog/useWorkspaceInviteDialog';

import {
  WorkspaceItem,
  WorkspaceCurrentItem,
  WorkspaceItemDetails,
  WorkspaceItemDetailsName,
} from '../styles';
import { toTitleCase } from '../utils';
import { WorkspaceMemberCount } from '../WorkspaceMemberCount';
import { WorkspaceName } from '../WorkspaceName';

interface WorkspaceCurrentSelectionProps {
  currentWorkspace: MinimumWorkspace;
}

export const WorkspaceCurrentSelection: React.FC<
  WorkspaceCurrentSelectionProps
> = ({ currentWorkspace }) => {
  const router = useRouter();

  const { DialogComponent, dialogProps, openDialog } =
    useWorkspaceInviteDialog();

  const showUpgradeModal = useSubscriptionStore(
    subscriptionStoreSelector.showUpgradeModal
  );

  const handleUpgradeClick = () => {
    showUpgradeModal('noIncentiveUpgrade', {
      trackingLabel: 'sidebarUpgradeButton',
    });
  };

  const handleSettingsClick = () => {
    router?.push(`/workspace/${currentWorkspace.id}/settings`);
  };

  return (
    <>
      <DialogComponent {...dialogProps} />
      <WorkspaceCurrentItem>
        <WorkspaceItem>
          <Avatar
            fallback={currentWorkspace.name}
            round="square"
            variant="photo"
          />
          <WorkspaceItemDetails>
            <WorkspaceItemDetailsName>
              <WorkspaceName workspace={currentWorkspace} />

              <WorkspaceMemberCount
                memberCount={currentWorkspace.memberCount}
              />
            </WorkspaceItemDetailsName>
            <div>
              <Badge size="m" text={toTitleCase(currentWorkspace.plan)} />
            </div>
          </WorkspaceItemDetails>
        </WorkspaceItem>
        <Popover.Close>
          <Button
            data-disable-nprogress={true}
            leftIcon={IconSettings}
            name="workspaceSelectionSettings"
            size="s"
            variant="ghost"
            width="100%"
            onClick={handleSettingsClick}
          >
            <Button.Label justifyContent="flex-start">
              Team Settings
            </Button.Label>
          </Button>
        </Popover.Close>

        <Button
          data-disable-nprogress={true}
          leftIcon={IconAddUser}
          name="workspaceSelectionAddUser"
          size="s"
          variant="ghost"
          width="100%"
          onClick={() => openDialog({ isCreation: false })}
        >
          <Button.Label justifyContent="flex-start">
            Invite Members
          </Button.Label>
          <Badge size="s" text="New" variant="highlight" />
        </Button>
        <SubscriptionUpgradeAccess>
          <BillingAdminAccess>
            <Button
              data-disable-nprogress={true}
              leftIcon={IconPremiumColored}
              name="upgradePlanButton"
              size="s"
              variant="ghost"
              width="100%"
              onClick={handleUpgradeClick}
            >
              <Button.Label justifyContent="flex-start">
                Upgrade Plan
              </Button.Label>
            </Button>
          </BillingAdminAccess>
        </SubscriptionUpgradeAccess>
      </WorkspaceCurrentItem>
    </>
  );
};
